import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../api/client';
import { VoiceCallPage } from '../api/voiceCalls';
import { PageHeader, PageShell, PaginationBar, StatusBadge } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';
import { voiceCallDate, voiceCallDuration } from '../utils/voiceCalls';
import './VoiceCalls.css';

export function VoiceCallsPage() {
  const { request } = useApiClient();
  const { t, language } = useI18n();
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') || '').trim();
  const rawPage = params.get('page') || '0';
  const pageNumber = /^\d+$/.test(rawPage) && Number.isSafeInteger(Number(rawPage)) ? Number(rawPage) : 0;
  const [search, setSearch] = React.useState(q);
  const [page, setPage] = React.useState<VoiceCallPage | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [refresh, setRefresh] = React.useState(0);
  React.useEffect(() => { setSearch(q); }, [q]);
  React.useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const query = new URLSearchParams({ page: String(pageNumber), size: '25' });
    if (q) query.set('q', q);
    setLoading(true); setError(false); setPage(null);
    request<VoiceCallPage>(`/admin/voice-calls?${query}`, { method: 'GET', signal: controller.signal })
      .then((value) => { if (active) setPage(value); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [request, pageNumber, q, refresh]);
  const navigatePage = (number: number, query = q) => {
    const next = new URLSearchParams();
    if (query.trim()) next.set('q', query.trim());
    if (number) next.set('page', String(number));
    setParams(next);
  };
  return <PageShell>
    <PageHeader title={t('voiceCalls.title')} description={t('voiceCalls.description')}
      actions={<button className="btn btn-neutral" onClick={() => setRefresh((v) => v + 1)}>{t('action.refresh')}</button>} />
    <form className="admin-panel voice-search" role="search" onSubmit={(event) => { event.preventDefault(); navigatePage(0, search); }}>
      <label htmlFor="voice-call-search" className="sr-only">{t('voiceCalls.searchLabel')}</label>
      <input id="voice-call-search" className="form-control" type="search" placeholder={t('voiceCalls.searchPlaceholder')} value={search} onChange={(event) => setSearch(event.target.value)} />
      <button className="btn btn-primary" type="submit">{t('voiceCalls.search')}</button>
    </form>
    {error && <div role="alert" className="voice-alert">{t('voiceCalls.loadError')}</div>}
    <section className="admin-panel voice-list" aria-label={t('voiceCalls.title')} aria-busy={loading}>
      {loading && <p role="status" className="voice-empty">{t('voiceCalls.loading')}</p>}
      {!loading && !error && page?.content.length === 0 && <p className="voice-empty">{t('voiceCalls.empty')}</p>}
      {!loading && !error && !!page?.content.length && <table className="voice-table">
        <thead><tr>{['call', 'caller', 'started', 'duration', 'firstMessage', 'tools'].map((key) => <th key={key} scope="col">{t(`voiceCalls.${key}`)}</th>)}</tr></thead>
        <tbody>{page.content.map((call) => <tr key={call.id}>
          <td data-label={t('voiceCalls.call')}><Link className="voice-link voice-id" to={`/voice-calls/${encodeURIComponent(call.id)}`}>{call.id}</Link>
            {!call.reportReceived && <div className="mt-2"><StatusBadge tone="amber">{t('voiceCalls.pending')}</StatusBadge></div>}</td>
          <td data-label={t('voiceCalls.caller')}>
            {call.userId && <Link className="voice-link" to={`/users/${encodeURIComponent(call.userId)}`}>{call.userFirstName || call.userId}</Link>}
            <div>{call.customerNumber || t('voiceCalls.unknownCaller')}</div>
          </td>
          <td data-label={t('voiceCalls.started')}><time dateTime={call.startedAt || call.firstSeen || undefined}>{voiceCallDate(call.startedAt || call.firstSeen, language)}</time>{!call.startedAt && call.firstSeen && <div className="voice-muted">{t('voiceCalls.firstSeen')}</div>}</td>
          <td data-label={t('voiceCalls.duration')}>{voiceCallDuration(call.startedAt, call.endedAt)}</td>
          <td data-label={t('voiceCalls.firstMessage')} className="voice-first-message">{call.firstUserText || '—'}</td>
          <td data-label={t('voiceCalls.tools')}><span>{call.toolCalls}</span>{call.failedTools > 0 && <div className="mt-2"><StatusBadge tone="red">{t('voiceCalls.failedCount', { count: call.failedTools })}</StatusBadge></div>}</td>
        </tr>)}</tbody>
      </table>}
      <PaginationBar loading={loading || error || !page} pageNumber={pageNumber} pageSize={25} displayPage={page || undefined} rowCount={page?.content.length || 0}
        totalElements={page?.totalElements} isLast={!page || page.number + 1 >= page.totalPages}
        onPrevious={() => navigatePage(Math.max(0, pageNumber - 1))} onNext={() => navigatePage(pageNumber + 1)}
        previousLabel={t('action.previous')} nextLabel={t('action.next')} />
    </section>
  </PageShell>;
}
